import { useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const INPUT = "mt-1.5 w-full rounded-xl border border-[#E2D7C3] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#8C2220]/30";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", form); setSent(true); } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-12" data-testid="contact-page">
      <div className="lg:col-span-5">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C2220]">Get in touch</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-bold leading-tight">We'd love to hear from you</h1>
        <p className="mt-5 text-slate-600 leading-relaxed">Questions about an order, a custom hamper, corporate gifts or a product you'd like us to stock? Send us a note and we'll reply by email, usually within one working day.</p>
        <p className="mt-6 text-sm text-slate-500">Have your order number handy? You can also <a href="/track" className="underline text-[#8C2220]">track your order</a>.</p>
      </div>
      <div className="lg:col-span-7">
        {sent ? (
          <div className="rounded-3xl bg-white border border-[#EDE4D3] p-10 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <p className="mt-4 font-serif text-2xl font-semibold">Thank you, {form.name.split(" ")[0]}!</p>
            <p className="mt-2 text-slate-600">Your message is on its way. We'll reply to {form.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-white border border-[#EDE4D3] p-6 sm:p-8 grid sm:grid-cols-2 gap-4" data-testid="contact-form">
            <label className="text-sm"><span className="text-slate-600">Your name</span><input required value={form.name} onChange={set("name")} className={INPUT} data-testid="contact-name-input" /></label>
            <label className="text-sm"><span className="text-slate-600">Email</span><input required type="email" value={form.email} onChange={set("email")} className={INPUT} data-testid="contact-email-input" /></label>
            <label className="text-sm sm:col-span-2"><span className="text-slate-600">Subject (optional)</span><input value={form.subject} onChange={set("subject")} maxLength={150} className={INPUT} data-testid="contact-subject-input" /></label>
            <label className="text-sm sm:col-span-2"><span className="text-slate-600">Message</span><textarea required minLength={5} rows={6} value={form.message} onChange={set("message")} maxLength={5000} className={INPUT} data-testid="contact-message-input" /></label>
            <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <button disabled={busy} className="sm:col-span-2 rounded-full bg-[#8C2220] text-white py-4 font-semibold flex items-center justify-center gap-2 hover:bg-[#701A18] disabled:opacity-60 transition-colors" data-testid="contact-submit-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send message
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
